import React, { useEffect, useMemo, useState } from "react";
import { Image, StatusBar, View } from "react-native";
import MainContainer from "components/MainContainer";
import TeamBanner from "./components/TeamBanner";
import Header from "./components/Header";
import MyReservations from "./components/MyReservations";
import UpcomingGames from "./components/UpcomingGames";
import { useAppSelector } from "hooks/useAppSelector";
import { useAccount } from "hooks/useAccount";
import { setAutoReservePopup, getAutoReservePopup } from "utils/storageUtils";
import AutoReservePopup from "./components/AutoReservePopup";
import VerifyPhonePopup from "./components/VerifyPhonePopup";
import useCurrentUser from "hooks/useCurrentUser";
import { useIsFocused } from "@react-navigation/native";
import useIsSSO from "hooks/useIsSSO";
import Spacer from "components/Spacer";
import { useFetchPlanPackagePromotionsQuery } from "queries/planPackage.queries";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import PromotionAds from "./components/PromotionAds";
import TicketProvisioningStatusBanner from "./components/TicketProvisioningStatusBanner";

const SPONSORED_PLAN_IDS = [53];

export default function Dashboard() {
  const { tenantSubdomain } = useAppSelector((state) => state.tenant);
  const { account, accountEvents } = useAccount();
  const { currentUser, isPrimary } = useCurrentUser();
  const isFocused = useIsFocused();
  const { isSSO } = useIsSSO();

  const { data: promotions } = useFetchPlanPackagePromotionsQuery();
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();

  const [isAutoReservePopupVisible, setIsAutoReservePopupVisible] =
    useState(false);
  const [verifyPhonePopupVisible, setVerifyPhonePopupVisible] = useState(false);
  // returns true if all subscriptions in account are inoperative
  const accountHasNoOperativeSubscriptions = useMemo(() => {
    if (!account) return false;
    return !account.subscriptions.some((sub) => sub.operative === true);
  }, [account]);

  const sponsoredId = useMemo(() => {
    if (!account) return undefined;
    return account.subscriptions.find(
      (sub) => !sub.expired && SPONSORED_PLAN_IDS.includes(sub.planPackage.id)
    )?.planPackage.id;
  }, [account]);

  const handleAutoReserveClose = (doNotShowChecked: boolean) => {
    // if do not show checkbox is checked, then the value for autoReservePopup should be false be cause we don't want it to appear again
    // doNotShowChecked: true -> autoReservePopup: false
    // doNotShowChecked: false -> autoReservePopup: true
    setAutoReservePopup(!doNotShowChecked);
    setIsAutoReservePopupVisible(false);
  };

  // If account is not null and autoReserveProcessing is true, then show the auto reserve info popup
  useEffect(() => {
    if (!account) return;
    if (account.autoReserveProcessing) {
      const value = getAutoReservePopup();
      setIsAutoReservePopupVisible(value === null ? true : Boolean(value));
    }
  }, [account]);

  // If user is primary and phone is not verified, show the verify phone popup
  useEffect(() => {
    if (!isPrimary || !currentUser) return;
    if (currentUser.phoneGrandfathered) return;
    if (tenantConfig?.requirePhoneVerification === false) return;
    if (!currentUser.phoneVerified && isFocused) {
      setVerifyPhonePopupVisible(true);
    }
  }, [currentUser, isPrimary, isFocused, tenantConfig]);

  return (
    <MainContainer showHeader={isSSO}>
      <StatusBar barStyle="dark-content" backgroundColor="#fff" />

      <View style={{ padding: 15 }}>
        <Header />
        <TicketProvisioningStatusBanner />
        {promotions && promotions.length > 0 && (
          <PromotionAds promotions={promotions} />
        )}
        {accountEvents && account && accountEvents.length > 0 && (
          <Spacer>
            <MyReservations accountEvents={accountEvents} />
          </Spacer>
        )}

        {accountHasNoOperativeSubscriptions && (
          <Spacer>
            <TeamBanner tenantSubdomain={tenantSubdomain} />
          </Spacer>
        )}
        {sponsoredId !== undefined && (
          <Image
            source={{
              uri: `https://branding.fanrally.com/sponsors/${tenantSubdomain}/${sponsoredId}/logo.png`,
            }}
            style={{
              width: "100%",
              height: 75,
              resizeMode: "contain",
            }}
          ></Image>
        )}
        <UpcomingGames />
      </View>
      <AutoReservePopup
        visible={isAutoReservePopupVisible}
        okText="Close"
        onOk={(doNotShow: boolean) => handleAutoReserveClose(doNotShow)}
      />
      <VerifyPhonePopup
        visible={verifyPhonePopupVisible}
        onCancel={() => setVerifyPhonePopupVisible(false)}
      />
    </MainContainer>
  );
}
